import * as React from 'react';
import {
  DashboardDataTableColumnKind,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../../lib/messages';
import {
  loadedSelectionHeaderState,
  manageDialogFetchSize,
  mergeLoadedSelection,
  pageClientRows,
} from '../../../../../../lib/clientSelectionList';
import { useRewriteListsQuery, useSchainListsQuery } from '../../../../../supply/lists/hooks';
import {
  listsPageSizes,
  type ListsFilter,
  type RewriteList,
  type SchainList,
} from '../../../../../supply/lists/model';
import {
  IntegrationAttachedListType,
  type AttachedRewriteList,
  type AttachedSchainList,
} from '../../../integrationRewriteModel';

interface ManagedIntegrationList {
  rowKey: string;
  id: string;
  name: string;
  type: IntegrationAttachedListType;
  mode: string;
}

const defaultPageSize = 10;
const listsLookupFilter: ListsFilter = {
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
};

export function ManageIntegrationListsDialog(props: {
  open: boolean;
  selectedRewrites: AttachedRewriteList[];
  selectedSchains: AttachedSchainList[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: {
    listRewrites: AttachedRewriteList[];
    listSchains: AttachedSchainList[];
  }) => void;
}) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [draft, setDraft] = React.useState<ManagedIntegrationList[]>(() =>
    selectedListRows(props.selectedRewrites, props.selectedSchains),
  );
  const search = useDebouncedValue(
    searchTerm,
    (value) => {
      setSearchTerm(value);
      setPage(0);
    },
    650,
  );
  const rewriteQuery = useRewriteListsQuery(listsLookupFilter, props.open);
  const schainQuery = useSchainListsQuery(listsLookupFilter, props.open);
  const rows = React.useMemo(
    () => [
      ...(rewriteQuery.data?.content ?? []).map(toRewriteRow),
      ...(schainQuery.data?.content ?? []).map(toSchainRow),
    ],
    [rewriteQuery.data, schainQuery.data],
  );
  const paged = pageClientRows({
    items: rows,
    searchTerm,
    searchText: (item) => item.name,
    page,
    pageSize,
  });
  const pageRows = paged.items;
  const loading = rewriteQuery.isLoading || schainQuery.isLoading;
  const loadError = rewriteQuery.isError && schainQuery.isError;
  const partialFailure = rewriteQuery.isError !== schainQuery.isError;

  const selectedKeys = React.useMemo(() => new Set(draft.map((item) => item.rowKey)), [draft]);
  const headerState = loadedSelectionHeaderState(
    selectedKeys,
    paged.filteredItems.map((item) => item.rowKey),
  );

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<ManagedIntegrationList>([
        {
          id: 'selected',
          kind: DashboardDataTableColumnKind.Selection,
          checked: (item) => selectedKeys.has(item.rowKey),
          label: (item) => item.name,
          headerChecked: headerState.checked,
          headerDisabled: headerState.disabled,
          headerLabel: dashboardT('common.selectAll'),
          onHeaderChange: (checked) => {
            setDraft((current) =>
              mergeLoadedSelection(
                current,
                paged.filteredItems,
                checked,
                (item) => item.rowKey,
                (item) => item.rowKey,
                (item) => item,
              ),
            );
          },
          onChange: (item, checked) => {
            setDraft((current) =>
              checked
                ? [...current.filter((candidate) => candidate.rowKey !== item.rowKey), item]
                : current.filter((candidate) => candidate.rowKey !== item.rowKey),
            );
          },
        },
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('integrations.rewrite.name'),
          value: (item) => item.name,
        },
        {
          id: 'type',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('integrations.rewrite.type'),
          value: (item) => attachedListTypeLabel(item.type),
        },
        {
          id: 'mode',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('integrations.rewrite.mode'),
          value: (item) => item.mode,
        },
      ]),
    [headerState.checked, headerState.disabled, paged.filteredItems, selectedKeys],
  );

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DashboardTableDialogContent>
        <DashboardTableDialogHeader title={dashboardT('integrations.rewrite.manageLists')} />
        <DashboardTableDialogBody>
          <DashboardTableDialogDataTable
            tableLabel={dashboardT('integrations.rewrite.manageLists')}
            items={pageRows}
            columns={columns}
            getRowKey={(item) => item.rowKey}
            loading={loading}
            errorMessage={loadError ? dashboardT('lists.loadError') : undefined}
            emptyMessage={dashboardT('lists.empty')}
            minWidthClassName="min-w-[680px]"
            toolbar={
              <DashboardTableSearchInput
                aria-label={dashboardT('integrations.rewrite.searchLists')}
                placeholder={dashboardT('integrations.rewrite.searchLists')}
                value={search.draft}
                onChange={(event) => {
                  search.setDraft(event.target.value);
                }}
              />
            }
            filterToolbar={
              partialFailure ? (
                <p role="status" className="text-warning-foreground text-sm">
                  {dashboardT('lists.partialLoadError')}
                </p>
              ) : undefined
            }
            pagination={{
              value: {
                page: paged.page,
                pageSize,
                totalItems: paged.totalItems,
              },
              pageSizes: listsPageSizes,
              disabled: loading,
              onChange: (next) => {
                setPage(next.page);
                setPageSize(next.pageSize);
              },
            }}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {dashboardT('integrations.rewrite.close')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              props.onConfirm({
                listRewrites: draft
                  .filter((item) => item.type === IntegrationAttachedListType.Rewrite)
                  .map(({ id, name }) => ({ id, name })),
                listSchains: draft
                  .filter((item) => item.type === IntegrationAttachedListType.Schain)
                  .map(({ id, name, mode }) => ({
                    id,
                    name,
                    ...(mode === '-' ? {} : { type: mode as SchainList['type'] }),
                  })),
              });
              props.onOpenChange(false);
            }}
          >
            {dashboardT('integrations.rewrite.updateLists')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}

function toRewriteRow(item: RewriteList): ManagedIntegrationList {
  return {
    rowKey: rowKey(IntegrationAttachedListType.Rewrite, item.id),
    id: item.id,
    name: item.name,
    type: IntegrationAttachedListType.Rewrite,
    mode: '-',
  };
}

function selectedListRows(
  rewrites: AttachedRewriteList[],
  schains: AttachedSchainList[],
): ManagedIntegrationList[] {
  return [
    ...rewrites.map((item) => ({
      rowKey: rowKey(IntegrationAttachedListType.Rewrite, item.id),
      id: item.id,
      name: item.name ?? item.id,
      type: IntegrationAttachedListType.Rewrite,
      mode: '-',
    })),
    ...schains.map((item) => ({
      rowKey: rowKey(IntegrationAttachedListType.Schain, item.id),
      id: item.id,
      name: item.name ?? item.id,
      type: IntegrationAttachedListType.Schain,
      mode: item.type ?? '-',
    })),
  ];
}

function toSchainRow(item: SchainList): ManagedIntegrationList {
  return {
    rowKey: rowKey(IntegrationAttachedListType.Schain, item.id),
    id: item.id,
    name: item.name,
    type: IntegrationAttachedListType.Schain,
    mode: item.type,
  };
}

function rowKey(type: IntegrationAttachedListType, id: string) {
  return `${type}:${id}`;
}

function attachedListTypeLabel(type: IntegrationAttachedListType) {
  return type === IntegrationAttachedListType.Rewrite
    ? dashboardT('lists.tabs.rewrite')
    : dashboardT('lists.tabs.schain');
}
